import VisibilityIcon from '@mui/icons-material/Visibility';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import MuiLink from '@mui/material/Link';
import { useTheme } from '@mui/material/styles';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { RowActionsMenu } from './RowActionsMenu';
import { ColumnDef } from './ServiceTable.types';
import { SlaCell } from './SlaCell';
import { PriorityDot } from '../../atoms/PriorityDot';
import { StatusBadge } from '../../atoms/StatusBadge';

export interface ServiceTableRowProps {
  row: Record<string, unknown>;
  columns: ColumnDef[];
  selected: boolean;
  even: boolean;
  onClick: () => void;
  onShiftClick: () => void;
  onPreview: () => void;
  onAction: (action: string) => void;
}

const CELL_SX = {
  fontSize: '11px',
  py: 0.25,
  px: 1,
  borderBottom: '1px solid',
  borderColor: 'v4.border',
} as const;

export const ServiceTableRow: React.FC<ServiceTableRowProps> = React.memo(({
  row,
  columns,
  selected,
  even,
  onClick,
  onShiftClick,
  onPreview,
  onAction,
}) => {
  const theme = useTheme();
  const serviceId = row.serviceId as number | undefined;

  const handleRowClick = (e: React.MouseEvent) => {
    if (e.shiftKey) {
      onShiftClick();
    } else {
      onClick();
    }
  };

  const getRowBg = () => {
    if (selected) return theme.palette.v4.tableSelected;
    if (even) return theme.palette.v4.tableEven;
    return 'background.paper';
  };

  const getHoverBg = () => {
    if (selected) return theme.palette.v4.tableSelectedHover;
    return theme.palette.v4.tableHover;
  };

  const logoPic = row.logoPic as string | undefined;

  const renderCell = (col: ColumnDef) => {
    if (col.render) return col.render(row);

    const value = row[col.key];

    if (col.key === 'serviceId' && serviceId != null) {
      return (
        <MuiLink
          component={RouterLink}
          to={`/services/${serviceId}`}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          sx={{ color: theme.palette.v4.accent, fontWeight: 600, textDecoration: 'none' }}
        >
          {String(serviceId)}
        </MuiLink>
      );
    }

    if (col.key === 'serviceStatusName' && typeof value === 'string') {
      return <StatusBadge status={value} size="small" />;
    }

    if (col.key === 'priorityCode' && typeof value === 'string') {
      const priorityObj = row.priority as Record<string, unknown> | undefined;
      const priorityColor = typeof priorityObj?.color === 'string' ? priorityObj.color : undefined;
      return <PriorityDot priority={value} color={priorityColor} />;
    }

    // SLA columns: render as colored burndown bars
    if (col.key === 'clientSLASummary' || col.key === 'opsSLASummary') {
      return <SlaCell value={value} />;
    }

    // Client column: show logo if available, otherwise text
    if (col.key === 'clientHierarchy' || col.key === 'clientName') {
      const text = value != null ? String(value) : '';
      if (logoPic) {
        return (
          <Tooltip title={text} placement="top" arrow>
            <Box
              component="img"
              src={logoPic}
              alt={text}
              sx={{ height: 22, maxWidth: 80, objectFit: 'contain' }}
            />
          </Tooltip>
        );
      }
      return text;
    }

    // Primary allocation: show red "Unallocated" badge if empty
    if (col.key === 'primaryAllocation') {
      const text = value != null ? String(value).trim() : '';
      if (!text) {
        return (
          <Box
            component="span"
            sx={{
              fontSize: '11px',
              fontWeight: 600,
              color: 'common.white',
              bgcolor: 'error.main',
              px: 1,
              py: '2px',
              borderRadius: '4px',
            }}
          >
            Unallocated
          </Box>
        );
      }
      return text;
    }

    if (col.key === 'creationNotes' || col.key === 'lastUpdatedNote') {
      return (
        <Box
          sx={{
            maxWidth: 180,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={typeof value === 'string' ? value : ''}
        >
          {value != null ? String(value) : ''}
        </Box>
      );
    }

    return value != null ? String(value) : '';
  };

  return (
    <TableRow
      onClick={handleRowClick}
      sx={{
        bgcolor: getRowBg(),
        cursor: 'pointer',
        '&:hover': { bgcolor: getHoverBg() },
        transition: 'background-color 0.15s ease',
      }}
    >
      <TableCell padding="checkbox" sx={CELL_SX}>
        <Checkbox
          size="small"
          checked={selected}
          onClick={(e) => e.stopPropagation()}
          onChange={() => onClick()}
          slotProps={{ input: { 'aria-label': `Select service ${serviceId ?? ''}` } }}
        />
      </TableCell>
      {columns.map((col) => (
        <TableCell
          key={col.key}
          sx={{
            ...CELL_SX,
            ...(col.width ? { minWidth: col.width, maxWidth: col.width } : {}),
          }}
        >
          {renderCell(col)}
        </TableCell>
      ))}
      <TableCell
        sx={{
          ...CELL_SX,
          width: 70,
          position: 'sticky',
          right: 0,
          bgcolor: 'inherit',
          zIndex: 1,
          boxShadow: '-4px 0 8px rgba(0,0,0,0.04)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onPreview(); }}
            aria-label={`Preview service ${serviceId ?? ''}`}
          >
            <VisibilityIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <RowActionsMenu
            serviceId={serviceId ?? 0}
            onAction={onAction}
          />
        </Box>
      </TableCell>
    </TableRow>
  );
});

ServiceTableRow.displayName = 'ServiceTableRow';
